import React from "react";
import styled from "styled-components";
import placeholderImg from "../../../assets/images/placeholder-img.svg";
import {
  borders,
  breakpoints,
  colors,
  fonts,
  media,
  scrollbars,
} from "../../common/mixins";
import Button from "../../common/Button";
import { ProductAdded, PriceStrike } from "../../icons";
import {
  ProductOption,
  StoreProduct,
} from "../../../stores/useReservationStore";
import { useReservationResources } from "../../../pageComponents/reservationPage/hooks/useReservationResources";
import {
  getMinQty,
  getStockHeadroom,
  getScopedMaxQty,
  getScopedQty,
} from "../../../pageComponents/reservationPage/utils/productQuantities";

// The card is rendered in the carousel, and its pieces (media, info, price,
// actions) are reused by the mobile product drawer and the description modal
// in products.tsx, so they are exported individually.

const Card = styled.div<{ $clickable?: boolean }>`
  width: 100%;
  padding: ${(props) => (props.$clickable ? "0" : "8px 8px 16px 8px")};
  border-radius: 8px;
  position: relative;
  display: flex;
  flex-direction: column;
  cursor: ${(props) => (props.$clickable ? "pointer" : "default")};
  ${(props) => (props.$clickable ? "" : borders.lightGrayBorder())};
  gap:8px;
`;

const ProductName = styled.p`
  ${fonts.normalText()}
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  margin: 0;
  line-height: normal;
`;

export const ProductDescription = styled.p`
  ${fonts.normalText()};
  margin: 0;

  ${media.below(breakpoints.tablet)} {
    color: ${colors.formDefault};
  }
`;

const ProductDescriptionLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  text-align: left;
  ${fonts.lightGrayText()};
  color: #696781;
  text-decoration-line: underline;
  text-decoration-style: solid;
  text-decoration-skip-ink: none;
  text-decoration-thickness: auto;
  text-underline-offset: auto;
  text-underline-position: from-font;
`;

export const Price = styled.div`
  ${fonts.smallTitle()}
`;

const PriceRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const OriginalPriceWrapper = styled.span`
  position: relative;
  display: inline-block;

  svg {
    position: absolute;
    top: 50%;
    left: 0;
    width: 100%;
    transform: translateY(-50%);
    pointer-events: none;
  }
`;

const OriginalPrice = styled.span`
  ${fonts.normalTextSmall()};
  font-size: 16px;
`;

const ProductInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ProductInfoAndActions = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 24px;
`;

export const ProductActionsWrapper = styled.div`
  flex: 0 0 auto;
  margin-top: auto;
`;

const ProductImage = styled.img`
  max-width: 100%;
  display: block;
  border-radius: 8px;
`;

const ImageContainer = styled.div<{ $bordered?: boolean }>`
  position: relative;
  aspect-ratio: 1/1;
  width: 100%;
  border-radius: 8px;
  overflow: hidden;
  background-color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  ${(props) => (props.$bordered ? borders.lightGrayBorder() : "")}
`;

type StatusKey = "offer" | "few" | "age" | "soldout";

const statusStyles: Record<StatusKey, { background: string; color: string }> = {
  offer: { background: "#D9F7E8", color: "#0D0C1C" },
  few: { background: "#FEE7C4", color: "#0D0C1C" },
  age: { background: "#FFF4B6", color: "#0D0C1C" },
  soldout: { background: "#A8ABAF", color: "#FFF" },
};

const StatusLabel = styled.div<{ $statusKey: StatusKey }>`
  position: absolute;
  top: 6px;
  left: 6px;
  padding: 2px 4px;
  border-radius: 4px;
  line-height: 16px;
  ${fonts.normalTextSmall()};
  background: ${(props) => statusStyles[props.$statusKey].background};
  color: ${(props) => statusStyles[props.$statusKey].color};
`;

// Positioning inside the description modal is set from that side, in
// products.tsx (ProductDetailsInfoColumn).
export const OptionsPopup = styled.div<{ $isMobile?: boolean }>`
  position: absolute;
  padding: 12px;
  bottom: 0;
  left: 0;
  right: 0;
  background: white;
  border-radius: 8px;
  ${borders.lightGrayBorder};
  z-index: 5;
  gap: 4px;
  display: flex;
  flex-direction: column;
  max-height: 75%;
  overflow-y: auto;
  margin: ${(props) => (props.$isMobile ? "0 16px 24px 16px" : "0 8px 16px 8px")};

  ${scrollbars.thin()}
`;

const PopupOption = styled.button`
  width: 100%;
  padding: 8px 8px;
  border: none;
  background: white;
  cursor: pointer;
  display: flex;
  gap: 4px;
  ${fonts.normalText()};
  line-height: 16px;
  border-radius: 4px;

  @media (hover: hover) {
    &:not(:disabled):hover {
      background: ${colors.menuItemHovered};
    }
  }

  &:not(:disabled):active {
    background: ${colors.menuItemHovered};
  }

  &:focus:not(:focus-visible) {
    outline: none;
  }

  &:disabled {
    cursor: default;
    color: #a8abaf;
  }
`;

const OptionName = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: left;
`;

const OptionPrice = styled.span`
  white-space: nowrap;
  flex-shrink: 0;
`;

function getEffectivePrice(option: ProductOption): number {
  return option.discountedPrice != null && option.discountedPrice < option.price
    ? option.discountedPrice
    : option.price;
}

/**
 * Badge reports STOCK — what the shop has left, net of this cart — and nothing
 * about the customer's own limits. "Sold out" must mean there is none to be had,
 * not that this order reached `maximumOrder`: a product with 200 in stock and a
 * cap of 2 would otherwise read "Few products left" before anyone touched it,
 * and "Sold out" after two. Hitting your own cap is the Add button's job, and it
 * already disables itself (see getAvailableOptions).
 */
function getStatusKey(
  product: StoreProduct,
  options: ProductOption[],
): StatusKey | null {
  const hasDiscount = options.some((o) => o.discountedPrice < o.price);
  const remainingQuantity = options.reduce(
    (sum, o) => sum + getStockHeadroom(product, o),
    0,
  );

  if (remainingQuantity === 0) return "soldout";
  if (remainingQuantity <= 5) return "few";
  if (hasDiscount) return "offer";
  if (product.isAgeRestricted) return "age";
  return null;
}

function getStatusText(
  statusKey: StatusKey | null,
  r: { SoldOut: string; FewProductsLeft: string; Offer: string },
): string {
  switch (statusKey) {
    case "soldout":
      return r.SoldOut;
    case "few":
      return r.FewProductsLeft;
    case "offer":
      return r.Offer;
    case "age":
      return "18+";
    default:
      return "";
  }
}

/**
 * Options that can still be added to this scope (a single ticket when
 * `ticketId` is given, the order otherwise): in stock, room left in the
 * scope's own ceiling, and not misconfigured with a minimum above that
 * ceiling. "Don't offer what can't be satisfied" — an option with 1 unit
 * left against a minimum of 2 disappears rather than baiting the user into
 * a dead end.
 */
export function getAvailableOptions(
  product: StoreProduct,
  ticketId?: number,
  orderedTicketIds?: number[],
): ProductOption[] {
  return product.productOptions.filter((o) => {
    const scopedMax = getScopedMaxQty(product, o, ticketId, orderedTicketIds);
    return (
      o.quantity > 0 &&
      scopedMax > getScopedQty(product, o.id, ticketId) &&
      getMinQty(o) <= scopedMax
    );
  });
}

interface ProductMediaProps {
  product: StoreProduct;
  bordered?: boolean;
}

export function ProductMedia({ product, bordered }: ProductMediaProps) {
  const r = useReservationResources();
  const statusKey = getStatusKey(product, product.productOptions);
  const statusText = getStatusText(statusKey, r);

  return (
    <ImageContainer $bordered={bordered}>
      <ProductImage
        src={product.images[0]?.imageUrl || placeholderImg}
        alt={product.name}
      />
      {statusKey ? (
        <StatusLabel id="label" $statusKey={statusKey}>
          {statusText}
        </StatusLabel>
      ) : null}
    </ImageContainer>
  );
}

interface ProductPriceProps {
  options: ProductOption[];
}

export function ProductPrice({ options }: ProductPriceProps) {
  const r = useReservationResources();
  const effectivePrices = options.map(getEffectivePrice);
  const lowestPrice = Math.min(...effectivePrices);
  const showFromPrice =
    options.length > 1 &&
    !effectivePrices.every((p) => p === effectivePrices[0]);
  const hasSingleDiscount =
    options.length === 1 && effectivePrices[0] < options[0].price;

  if (hasSingleDiscount) {
    return (
      <PriceRow>
        <OriginalPriceWrapper>
          <OriginalPrice id="product-original-price">
            {options[0].price}€
          </OriginalPrice>
          <PriceStrike preserveAspectRatio="none" />
        </OriginalPriceWrapper>
        <Price id="product-discount">{options[0].discountedPrice}€</Price>
      </PriceRow>
    );
  }

  return (
    <Price id="product-price">
      {showFromPrice ? `${r.From} ` : ""}
      {lowestPrice}€
    </Price>
  );
}

interface ProductInfoSectionProps {
  product: StoreProduct;
  showPrice?: boolean;
  descriptionDisplay?: "full" | "link" | "hidden";
  descriptionRef?: React.Ref<HTMLParagraphElement>;
  onDescriptionClick?: (product: StoreProduct) => void;
}

export function ProductInfoSection({
  product,
  showPrice = true,
  descriptionDisplay = "full",
  descriptionRef,
  onDescriptionClick,
}: ProductInfoSectionProps) {
  const r = useReservationResources();

  return (
    <ProductInfo>
      <div>
        <ProductName id="product-name">{product.name}</ProductName>
        {product.description && descriptionDisplay === "full" ? (
          <ProductDescription id="product-description" ref={descriptionRef}>
            {product.description}
          </ProductDescription>
        ) : null}
        {product.description && descriptionDisplay === "link" ? (
          <ProductDescriptionLink
            id="product-description-link"
            type="button"
            onClick={() => onDescriptionClick?.(product)}
          >
            {r.Description}
          </ProductDescriptionLink>
        ) : null}
      </div>

      {showPrice ? <ProductPrice options={product.productOptions} /> : null}
    </ProductInfo>
  );
}

interface ProductActionsProps {
  product: StoreProduct;
  currency: string;
  isAdded: boolean;
  availableOptions: ProductOption[];
  isPopupOpen: boolean;
  popupRef: React.RefObject<HTMLDivElement>;
  idSuffix: string;
  isMobile?: boolean;
  // False while a drawer/modal shows the same product's actions, so the
  // options popup only ever renders in one place.
  interactive?: boolean;
  onAddClick: (product: StoreProduct) => void;
  onOptionSelect: (productId: number, optionId: number) => void;
  onClosePopup: () => void;
}

/**
 * Owns the Add button + options popup, including the accessibility wiring
 * that has to reach into the shared `Button` component's rendered DOM node:
 * `Button` (components/common/Button.tsx) does not forward aria-* props or a
 * ref, and it is outside this file's territory, so aria-expanded/aria-haspopup
 * are applied imperatively to the underlying <button> instead.
 */
export function ProductActions({
  product,
  currency,
  isAdded,
  availableOptions,
  isPopupOpen,
  popupRef,
  idSuffix,
  isMobile,
  interactive = true,
  onAddClick,
  onOptionSelect,
  onClosePopup,
}: ProductActionsProps) {
  const r = useReservationResources();
  const options = product.productOptions;
  const hasOptionsMenu = options.length > 1;
  const showPopup = interactive && isPopupOpen;
  const addButtonWrapperRef = React.useRef<HTMLDivElement>(null);

  const popupDomId = `product-options-pop-up${idSuffix}`;

  React.useEffect(() => {
    const buttonEl = addButtonWrapperRef.current?.querySelector("button");
    if (!buttonEl) return;
    if (hasOptionsMenu) {
      buttonEl.setAttribute("aria-haspopup", "menu");
      buttonEl.setAttribute("aria-expanded", String(showPopup));
      buttonEl.setAttribute("aria-controls", popupDomId);
    } else {
      buttonEl.removeAttribute("aria-haspopup");
      buttonEl.removeAttribute("aria-expanded");
      buttonEl.removeAttribute("aria-controls");
    }
  }, [hasOptionsMenu, showPopup, popupDomId]);

  const isOptionAvailable = (optionId: number) =>
    availableOptions.some((o) => o.id === optionId);

  // Attached to the wrapper (not the popup) because focus stays on the Add
  // button itself right after it opens the popup — an Escape listener scoped
  // to the popup alone would never see that keydown.
  const handlePopupKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "Escape" || !showPopup) return;
    event.stopPropagation();
    onClosePopup();
    addButtonWrapperRef.current?.querySelector("button")?.focus();
  };

  return (
    <ProductActionsWrapper
      ref={showPopup ? popupRef : null}
      onKeyDown={handlePopupKeyDown}
    >
      {showPopup && (
        <OptionsPopup
          id={popupDomId}
          role="group"
          aria-label={product.name}
          $isMobile={isMobile}
        >
          {options.map((option) => (
            <PopupOption
              key={option.id}
              type="button"
              disabled={!isOptionAvailable(option.id)}
              onClick={() => onOptionSelect(product.id, option.id)}
            >
              <OptionName id="product-option-name">{option.name}</OptionName>
              <OptionPrice id="product-option-price">
                - {getEffectivePrice(option)}
                {currency}
              </OptionPrice>
            </PopupOption>
          ))}
        </OptionsPopup>
      )}
      <div ref={addButtonWrapperRef}>
        <Button
          id={`add-product-btn${idSuffix}`}
          type="button"
          variant="secondary"
          text={isAdded ? r.Added : r.Add}
          height="48px"
          fullWidth
          iconComponent={isAdded ? <ProductAdded /> : null}
          onClick={() => onAddClick(product)}
          disabled={availableOptions.length === 0 || isAdded}
        />
      </div>
    </ProductActionsWrapper>
  );
}

export interface ProductCardProps {
  product: StoreProduct;
  currency: string;
  isAdded: boolean;
  isPopupOpen: boolean;
  popupRef: React.RefObject<HTMLDivElement>;
  idSuffix: string;
  ticketId?: number;
  // Needed to hold back stock the other tickets owe a compulsory minimum; a
  // stable reference from the store, so React.memo still holds.
  orderedTicketIds?: number[];
  // Mobile: the whole card opens the product drawer and the Add button lives
  // there instead. Desktop: the card shows Add and a "Description" link.
  isMobile: boolean;
  // False while the drawer/description modal is open, so the card's options
  // popup doesn't render behind it.
  isInteractive: boolean;
  onCardClick: (product: StoreProduct) => void;
  onDescriptionClick: (product: StoreProduct) => void;
  onAddClick: (product: StoreProduct) => void;
  onOptionSelect: (productId: number, optionId: number) => void;
  onClosePopup: () => void;
}

function ProductCardComponent({
  product,
  currency,
  isAdded,
  isPopupOpen,
  popupRef,
  idSuffix,
  ticketId,
  orderedTicketIds,
  isMobile,
  isInteractive,
  onCardClick,
  onDescriptionClick,
  onAddClick,
  onOptionSelect,
  onClosePopup,
}: ProductCardProps) {
  // Derived here rather than passed in: a fresh array from the parent on every
  // render would make the React.memo below inert.
  const availableOptions = getAvailableOptions(
    product,
    ticketId,
    orderedTicketIds,
  );

  return (
    <Card
      id="card"
      $clickable={isMobile}
      onClick={isMobile ? () => onCardClick(product) : undefined}
    >
      <ProductMedia product={product} bordered={isMobile} />
      <ProductInfoAndActions>
        <ProductInfoSection
          product={product}
          descriptionDisplay={isMobile ? "hidden" : "link"}
          onDescriptionClick={onDescriptionClick}
        />
        {!isMobile && (
          <ProductActions
            product={product}
            currency={currency}
            isAdded={isAdded}
            availableOptions={availableOptions}
            isPopupOpen={isPopupOpen}
            popupRef={popupRef}
            idSuffix={idSuffix}
            interactive={isInteractive}
            onAddClick={onAddClick}
            onOptionSelect={onOptionSelect}
            onClosePopup={onClosePopup}
          />
        )}
      </ProductInfoAndActions>
    </Card>
  );
}

export const ProductCard = React.memo(ProductCardComponent);
